import {
  ExcelExport,
  ExcelExportColumnProps
} from '@progress/kendo-react-excel-export';
import { Grid } from '@progress/kendo-react-grid';
import axios from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { RouteAnimation } from 'src/animations/animations';
import { Divider } from 'src/components/shared/Divider/Divider';
import { useReduxDispatch } from 'src/store';
import { alertError, alertSuccess } from 'src/store/alerts/alerts.actions';
import { postRequest } from 'src/store/api-handler';
import { fetchMixtureCandidates } from 'src/store/mixturecandidates/mixturecandidates.actions';
import {
  MixtureDetailDto,
  MixtureProductDto
} from 'src/store/mixtures/mixture';
import { mapMixtureDetailToEdit } from 'src/store/mixtures/mixture.map';
import {
  createMixture,
  editMixture,
  MixtureTypes
} from 'src/store/mixtures/mixtures.actions';
import { MixtureApi } from 'src/store/mixtures/mixtures.api';
import { ParameterSetDetailDto } from 'src/store/parametersets/parameterset';
import { ParameterSetApi } from 'src/store/parametersets/parametersets.api';
import { getCorrectedValueAtIndex } from 'src/store/products/product.utils';
import { RunSystemDto } from 'src/store/runsystems/runsystem';
import { download, getFileNameFromHeader } from 'src/utils/download';
import { push } from 'src/utils/navigation';
import { MixtureDetailsForm } from './MixtureDetailsForm';
import { MixtureParameterValues } from './MixtureParameterValues';
import { MixtureProducts } from './MixtureProducts';

export const MixturePage = () => {
  const dispatch = useReduxDispatch();
  const { id: idParam } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [mixture, setMixture] = useState<Nullable<MixtureDetailDto>>(null);
  const [parameterSet, setParameterSet] =
    useState<Nullable<ParameterSetDetailDto>>(null);
  const [exporter, setExporter] = useState<Nullable<ExcelExport>>(null);
  const grid = useRef<Grid>(null);
  const [showExportSpinner, setShowExportSpinner] = useState<boolean>(false);

  const [mixtureProducts, setMixtureProducts] = useState<MixtureProductDto[]>(
    []
  );

  useEffect(() => {
    const fetch = async () => {
      const response = await MixtureApi.getById(Number(idParam));
      if (response.ok) {
        setMixture(response.data);
      }
    };

    if (!!idParam) {
      fetch();
    }
  }, [idParam, dispatch]);

  useEffect(() => {
    if (mixture) {
      const fetchMixtureProductParameters = async () => {
        const response = await MixtureApi.getMixtureProductParameters(
          mixture.id!,
          mixture.parameterSetId!
        );
        if (response.ok) setMixtureProducts(response.data);
      };

      const fetchParameterSet = async () => {
        const response = await ParameterSetApi.getById(mixture.parameterSetId!);
        if (response.ok) setParameterSet(response.data);
      };

      fetchParameterSet();
      fetchMixtureProductParameters();
      dispatch(fetchMixtureCandidates(mixture.parameterSetId));
    } else {
      dispatch(fetchMixtureCandidates());
    }
  }, [mixture, dispatch]);

  const onMixtureProductsChanged = useCallback(
    (mixtureProducts: MixtureProductDto[]) => {
      setMixtureProducts(mixtureProducts);
    },
    []
  );

  const onCancel = () => {
    push('/mixtures', navigate, RouteAnimation.SlideLeft);
  };

  const onSubmit = async (mixture: MixtureDetailDto) => {
    const dto = mapMixtureDetailToEdit(mixture);
    dto.mixtureProducts = mixtureProducts;
    const result = dto.id
      ? await dispatch(editMixture(dto.id, dto))
      : await dispatch(createMixture(dto));
    if (result.type !== MixtureTypes.Failure) {
      onCancel();
    }
  };

  const onCreateRun = async (
    mixture: MixtureDetailDto,
    runSystem: RunSystemDto
  ) => {
    const { ok, error } = await postRequest(
      `/mixtures/${mixture.id}/runsystem/${runSystem.id}`,
      mixture
    );

    if (ok) {
      dispatch(alertSuccess(t('product.runCreated')));
    } else {
      dispatch(alertError(error?.errors?.detail ?? t('error.serverError')));
    }
  };

  const onInitExporter = (exporter: ExcelExport) => {
    setExporter(exporter);
  };

  const onExport = async () => {
    setShowExportSpinner(true);
    let mProducts: MixtureProductDto[] = [];
    mProducts = mixtureProducts.map((x: MixtureProductDto) => {
      (parameterSet?.parameterSetParameters ?? []).forEach((p, i) => {
        (x as MixtureProductDto & { [key: string]: number | null })[
          `parameter${x.parameters[i].parameterId}`
        ] = getCorrectedValueAtIndex(x.parameters, i);
      });
      return x;
    });
    exporter!.save(
      mProducts,
      grid.current?.columns as ExcelExportColumnProps[]
    );
    setShowExportSpinner(false);
  };

  const onDownloadMixtureData = async () => {
    axios({
      method: 'GET',
      responseType: 'arraybuffer',
      url: `/mixtures/${mixture?.id}/mixturedata`
    })
      .then(response => {
        const disposition = response.headers['content-disposition'];
        const contentType = response.headers['content-type'];
        const filename = getFileNameFromHeader(disposition);
        const blob = new Blob([response.data as Blob], {
          type: contentType
        });
        download(filename, blob);
      })
      .catch(() => {});
  };

  return (
    <React.Fragment>
      <MixtureDetailsForm
        mixture={mixture}
        onCancel={onCancel}
        onSubmit={onSubmit}
        onCreateRun={onCreateRun}
        onDownloadMixtureData={onDownloadMixtureData}
      />

      <Divider />

      <MixtureParameterValues mixture={mixture}></MixtureParameterValues>

      <Divider />

      <MixtureProducts
        mixture={mixture}
        parameterSet={parameterSet}
        onMixtureChange={setMixture}
        mixtureProducts={mixtureProducts}
        onMixtureProductsChanged={onMixtureProductsChanged}
        initExporter={onInitExporter}
        onExport={onExport}
        showExportSpinner={showExportSpinner}
        gridRef={grid}
      />
    </React.Fragment>
  );
};
